import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Check, ChevronDown, Search, X} from 'lucide-react';
import {useTranslation} from "react-i18next";

interface GasStationData {
  id: number;
  number: number;
  name: string;
  gasStationAdminName: string;
  totalFines: number;
  paidFines: number;
  cancelledFines: number;
  customer: string;
  internationalFee: number;
  ownerCount: number;
  numOfPaidTickets: number;
  numOfOpenTickets: number;
  numOfClosedTickets: number;
  numOfCancelledTickets: number;
  useClientDocs: boolean;
  beppoClient?: {
    id: number;
    name: string;
  };
  interruptAutoBilling: boolean;
  labels: Array<{
    id: number;
    name: string;
  }>;
  powerOfAttorney?: string;
  documentation?: string;
}

interface MultiSelectCheckboxProps {
  data: GasStationData[];
  value?: number[];
  onChange?: (value: number[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface GroupedData {
  [key: string]: GasStationData[];
}

export const MultiSelectCheckbox: React.FC<MultiSelectCheckboxProps> = ({
  data = [],
  value = [],
  onChange,
  className = "",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const groupedAndFilteredData = useMemo(() => {
    const filteredData = data.filter(item =>
      (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.number || '').toString().includes(searchTerm) ||
      (item.customer || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.gasStationAdminName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.labels || []).some(label => (label.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const grouped: GroupedData = {};
    
    filteredData.forEach(item => {
      if (!item.labels || item.labels.length === 0) {
        const noLabelText = t("Reports.Dashboard.NoLabel");
        if (!grouped[noLabelText]) {
          grouped[noLabelText] = [];
        }
        grouped[noLabelText].push(item);
      } else {
        item.labels.forEach(label => {
          const labelName = label.name || t("Reports.UnknownLabel");
          if (!grouped[labelName]) {
            grouped[labelName] = [];
          }
          grouped[labelName].push(item);
        });
      }
    });

    return grouped;
  }, [data, searchTerm]);

  const handleToggle = (itemId: number) => {
    const newValue = value.includes(itemId)
      ? value.filter(v => v !== itemId)
      : [...value, itemId];
    onChange?.(newValue);
  };

  const handleGroupToggle = (groupName: string) => {
    const groupItems = groupedAndFilteredData[groupName] || [];
    const groupItemIds = groupItems.map(item => item.id);
    
    const allSelected = groupItemIds.every(id => value.includes(id));
    
    if (allSelected) {
      const newValue = value.filter(id => !groupItemIds.includes(id));
      onChange?.(newValue);
    } else {
      const newValue = [...new Set([...value, ...groupItemIds])];
      onChange?.(newValue);
    }
  };

  const isGroupSelected = (groupName: string) => {
    const groupItems = groupedAndFilteredData[groupName] || [];
    const groupItemIds = groupItems.map(item => item.id);
    return groupItemIds.length > 0 && groupItemIds.every(id => value.includes(id));
  };

  const isGroupPartiallySelected = (groupName: string) => {
    const groupItems = groupedAndFilteredData[groupName] || [];
    const groupItemIds = groupItems.map(item => item.id);
    const selectedCount = groupItemIds.filter(id => value.includes(id)).length;
    return selectedCount > 0 && selectedCount < groupItemIds.length;
  };

  const handleRemove = (itemId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(value.filter(v => v !== itemId));
  };

  const getSelectedItems = () => {
    const selectedItems = value.map(id => {
      const item = data.find(d => d.id === id);
      return item;
    }).filter(Boolean) as GasStationData[];

    const fullySelectedGroups: string[] = [];
    const remainingItems: GasStationData[] = [];
    
    Object.entries(groupedAndFilteredData).forEach(([groupName, items]) => {
      const groupItemIds = items.map(item => item.id);
      const allGroupItemsSelected = groupItemIds.every(id => value.includes(id));
      
      if (allGroupItemsSelected && groupItemIds.length > 0) {
        fullySelectedGroups.push(groupName);
      } else {
        const individualItems = selectedItems.filter(item => 
          items.some(groupItem => groupItem.id === item.id)
        );
        remainingItems.push(...individualItems);
      }
    });

    const uniqueRemainingItems = remainingItems.filter((item, index, self) =>
      index === self.findIndex(t => t.id === item.id)
    );

    return { fullySelectedGroups, remainingItems: uniqueRemainingItems };
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    searchInputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`} style={{ minWidth: "450px"}}>
      <div
        ref={triggerRef}
        className={`
          flex w-full min-h-[40px] items-center justify-between gap-2 
          rounded-md border border-gray-300 bg-white px-3 py-2 
          text-sm cursor-pointer transition-all
          hover:bg-gray-50 focus:outline-none focus:ring-2 
          focus:ring-blue-500 focus:border-blue-500
          ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
          ${disabled ? 'cursor-not-allowed opacity-50' : ''}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            !disabled && setIsOpen(!isOpen);
          }
        }}
      >
        <div className="flex flex-1 flex-wrap items-center gap-1 overflow-hidden">
          {value.length === 0 ? (
            <span className="text-gray-500">{t("Reports.SelectGasStation")}</span>
          ) : (
            <>
              {(() => {
                const { fullySelectedGroups, remainingItems } = getSelectedItems();
                const totalToShow = 3;
                const itemsToShow: JSX.Element[] = [];

                fullySelectedGroups.slice(0, totalToShow).forEach(groupName => {
                  itemsToShow.push(
                    <span
                      key={`group-${groupName}`}
                      className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800"
                    >
                      {groupName}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGroupToggle(groupName);
                        }}
                        className="rounded-full hover:bg-gray-200 p-0.5"
                        aria-label={`Remove ${groupName} group`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  );
                });

                const remainingSpace = totalToShow - fullySelectedGroups.length;
                if (remainingSpace > 0) {
                  remainingItems.slice(0, remainingSpace).forEach(item => {
                    itemsToShow.push(
                      <span
                        key={item.id}
                        className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800"
                      >
                        {item.name || 'Unnamed Station'}
                        <button
                          onClick={(e) => handleRemove(item.id, e)}
                          className="rounded-full hover:bg-gray-200 p-0.5"
                          aria-label={`Remove ${item.name || 'station'}`}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    );
                  });
                }

                const totalShownGroups = Math.min(fullySelectedGroups.length, totalToShow);
                const totalShownItems = Math.min(remainingItems.length, totalToShow - totalShownGroups);
                const totalHiddenGroups = Math.max(0, fullySelectedGroups.length - totalShownGroups);
                const totalHiddenItems = Math.max(0, remainingItems.length - totalShownItems);
                const totalHidden = totalHiddenGroups + totalHiddenItems;

                return (
                  <>
                    {itemsToShow}
                    {totalHidden > 0 && (
                      <span className="text-xs text-gray-500">
                        +{totalHidden} more
                      </span>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
        <ChevronDown 
          size={16} 
          className={`shrink-0 text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </div>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 mt-1 rounded-md border border-gray-200 bg-white shadow-lg max-h-96 overflow-hidden"
          style={{ width: "100%" }}
        >
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t("Reports.SearchGasStation")}
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {Object.keys(groupedAndFilteredData).length === 0 ? (
              <div className="p-3 text-gray-500 text-center">
                No gas stations found
              </div>
            ) : (
              Object.entries(groupedAndFilteredData).map(([groupName, items]) => (
                <div key={groupName} className="p-1">
                  <button
                    onClick={() => handleGroupToggle(groupName)}
                    className={`
                      flex w-full items-center gap-2 px-2 py-2 text-sm 
                      transition-colors hover:bg-gray-100 bg-gray-50 border-b border-gray-200
                      ${isGroupSelected(groupName) ? 'bg-blue-50' : ''}
                    `}
                  >
                    <div
                      className={`
                        flex h-4 w-4 items-center justify-center rounded border 
                        transition-colors flex-shrink-0
                        ${isGroupSelected(groupName)
                          ? 'border-blue-600 bg-blue-600' 
                          : isGroupPartiallySelected(groupName)
                          ? 'border-blue-600 bg-blue-100'
                          : 'border-gray-300'
                        }
                      `}
                    >
                      {isGroupSelected(groupName) && (
                        <Check size={12} className="text-white" />
                      )}
                      {isGroupPartiallySelected(groupName) && (
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900">{groupName}</div>
                      <div className="text-xs text-gray-500">
                        {t("Reports.SelectAll")}
                      </div>
                    </div>
                  </button>
                  
                  {items.map((item) => {
                    const isSelected = value.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleToggle(item.id)}
                        className={`
                          flex w-full items-center gap-2 rounded py-2 text-sm 
                          transition-colors hover:bg-gray-100 pl-8 pr-2
                        `}
                      >
                        <div
                          className={`
                            flex h-4 w-4 items-center justify-center rounded border 
                            transition-colors flex-shrink-0
                            ${isSelected 
                              ? 'border-blue-600 bg-blue-600' 
                              : 'border-gray-300'
                            }
                          `}
                        >
                          {isSelected && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        <div className="flex-1 text-left overflow-hidden">
                          <div className="font-medium text-gray-900 truncate">{item.name || t("Reports.UnnamedStation")}</div>
                          <div className="text-xs text-gray-500 truncate">
                            #{item.number || 'N/A'} • {item.customer || t("Reports.NoCustomer")}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {value.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-3 py-2">
              <span className="text-xs text-gray-600">
                {value.length} {t("Reports.Selected")}
              </span>
              <button
                onClick={() => onChange?.([])}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {t("Reports.ClearAll")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectCheckbox;